// Replay-mode request handler: given an intercepted /api request, synthesize a
// Response from fixtures + overlay, without any backend.
//
// Routing is mostly generic by HTTP method:
//   - GET            -> recorded fixture (with session overrides applied)
//   - POST/PUT/...   -> benign success, recording word-status overrides
// with a few special cases (auth, translation) handled explicitly so the app
// boots straight into the authenticated UI and click-to-translate works.

import { lookupFixture } from './fixtures';
import { signature, pathSignature, normalizePath, parseApiUrl, isMediaUrl } from './signature';
import { overlay } from './overlay';
import { translateTerm } from './translation';

function json(body: unknown, status = 200): Response {
  return new Response(JSON.stringify(body), {
    status,
    headers: { 'Content-Type': 'application/json' },
  });
}

function parseBody(init?: RequestInit): Record<string, unknown> {
  const raw = init?.body;
  if (typeof raw !== 'string' || !raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

export function handleDemoRequest(method: string, url: string, init?: RequestInit): Response {
  const m = method.toUpperCase();
  const path = normalizePath(parseApiUrl(url)?.path ?? url);

  // Media (audio/epub) has no fixtures in the static demo — fail quietly so
  // <audio> elements and <img> just show nothing rather than hanging.
  if (isMediaUrl(url)) return new Response(null, { status: 404 });

  // --- Auth: always logged in, no setup, no login wall. ---
  if (path === '/api/auth/status') {
    return json({ authenticated: true, needsSetup: false, user: { username: 'Demo' } });
  }
  if (path === '/api/auth/login' || path === '/api/auth/setup') {
    return json({ authenticated: true });
  }
  if (path === '/api/auth/logout') {
    return json({ message: 'ok' });
  }

  // --- Click-to-translate: answer from the harvested dictionary. ---
  if (m === 'POST' && path === '/api/translation') {
    const body = parseBody(init);
    const text = typeof body.text === 'string' ? body.text : '';
    return json({ translatedText: translateTerm(text) });
  }

  // --- Writes: record status overrides, otherwise no-op success. ---
  if (m !== 'GET' && m !== 'HEAD') {
    return handleWrite(m, path, init);
  }

  // --- Reads: serve from fixtures with session overrides applied. ---
  const fixture = lookupFixture(signature(m, url), pathSignature(m, url));
  if (fixture) {
    return json(overlay.apply(fixture.body), fixture.status);
  }

  // Nothing recorded for this read. Return an empty-but-valid shape so the UI
  // degrades to an empty state instead of throwing.
  return json(emptyFallback(path));
}

function handleWrite(method: string, path: string, init?: RequestInit): Response {
  const body = parseBody(init);

  // Create a word (clicked an unknown term and saved it).
  if (method === 'POST' && path === '/api/words') {
    const wordId = overlay.createWord();
    if (body.status !== undefined) overlay.setWordStatus(wordId, body.status as number);
    return json({
      wordId,
      term: body.term ?? '',
      status: body.status ?? 0,
      translation: body.translation ?? '',
    });
  }

  // Update a word's status (e.g. mark Known).
  const wordUpdate = path.match(/^\/api\/words\/(-?\d+)$/);
  if (method === 'PUT' && wordUpdate) {
    overlay.setWordStatus(wordUpdate[1], (body.status as number) ?? 0);
    return json({ wordId: Number(wordUpdate[1]), ...body });
  }
  if (method === 'DELETE' && wordUpdate) {
    overlay.deleteWord(wordUpdate[1]);
    return json({ message: 'ok' });
  }

  // Everything else (SRS grades, settings, AI generation, batch ops, …) is a
  // backend-only action. Acknowledge it so the UI advances without erroring.
  return json({ message: 'ok' });
}

// Best-effort empty value for an unrecorded read. Most endpoints in this API
// return collections, and a collection-consumer crashes hard when handed a
// non-array (e.g. `texts.slice(...)`), whereas the object-consumers here use
// defensive access (`pick`, `?.`, `?? default`). So we default to an empty
// ARRAY and keep an allowlist of the known object/detail endpoints. (With real
// recorded fixtures this path is rarely hit — it only backstops gaps.)
function emptyFallback(path: string): unknown {
  const objectLike =
    /\/(texts|books|languages)\/\d+$/.test(path) || // a single detail entity
    /\/word-linking-status$/.test(path) ||
    /\/(usersettings|audio-storage-size)$/.test(path) ||
    /\/users\/(dashboard|statistics|reading-activity|listening-activity|known-words-activity)$/.test(path) ||
    /\/srs\/(stats|analytics)$/.test(path) ||
    /\/(health|ready|status)$/.test(path);
  return objectLike ? {} : [];
}
